/**
 * EcgridPackageTestPage - Test page using @ecgrid/ecg-react-components package
 *
 * This demonstrates all major components from the package:
 * - PortalLayout for standard layout (header, sidebar, footer)
 * - DataTable with DataTableFilters, InputFilter, SelectFilter
 * - DataTableColumnHeader for sortable columns
 *
 * Layout is handled by PortalLayout — no manual sidebar state or slot wiring needed.
 *
 * Uses CQRS pattern with mediator for data fetching
 */

import type { SampleUser } from '@/application/queries/sampleData';
import { PortalLayout } from '@/components/common/PortalLayout';
import {
  // DataTable Components
  DataTable,
  DataTableColumnHeader,
  DataTableFilters,
  InputFilter,
  SelectFilter,
} from '@/components/ui';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { useEcgridPackageTest } from './EcgridPackageTestPage.hook';

// ============================================================================
// Loading Component
// ============================================================================

interface LoadingStateProps {
  loadingText: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({ loadingText }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">{loadingText}</p>
    </div>
  </div>
);

// ============================================================================
// Error Component
// ============================================================================

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
  errorTitle: string;
  retryText: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, errorTitle, retryText }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4 p-6 max-w-md text-center">
      <div className="text-destructive text-4xl">⚠️</div>
      <h2 className="text-xl font-semibold">{errorTitle}</h2>
      <p className="text-muted-foreground">{error.message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        {retryText}
      </button>
    </div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const EcgridPackageTestPage = React.forwardRef<HTMLDivElement>((_, ref) => {
  // i18n translation hook
  const { t } = useTranslation(NS.ECGRID_PACKAGE_TEST);

  // Fetch data using hook (CQRS pattern)
  const { data, loading, error, refetch } = useEcgridPackageTest();

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Table columns definition
  const columns = useMemo<ColumnDef<SampleUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('EcgridPackageTest_Table_Column_Name')} />
        ),
      },
      {
        accessorKey: 'email',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('EcgridPackageTest_Table_Column_Email')}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('EcgridPackageTest_Table_Column_Status')}
          />
        ),
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          const statusColors: Record<string, string> = {
            active: 'bg-green-100 text-green-800',
            inactive: 'bg-gray-100 text-gray-800',
            pending: 'bg-yellow-100 text-yellow-800',
          };
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
        filterFn: (row, id, value) => {
          if (!value || (Array.isArray(value) && value.length === 0)) return true;
          const cellValue = (row.getValue(id) as string)?.toLowerCase();
          if (Array.isArray(value)) {
            return value.some((v) => v.toLowerCase() === cellValue);
          }
          return cellValue === (value as string).toLowerCase();
        },
      },
      {
        accessorKey: 'role',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('EcgridPackageTest_Table_Column_Role')} />
        ),
        filterFn: (row, id, value) => {
          if (!value || (Array.isArray(value) && value.length === 0)) return true;
          const cellValue = (row.getValue(id) as string)?.toLowerCase();
          if (Array.isArray(value)) {
            return value.some((v) => v.toLowerCase() === cellValue);
          }
          return cellValue === (value as string).toLowerCase();
        },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('EcgridPackageTest_Table_Column_CreatedAt')}
          />
        ),
      },
    ],
    [t]
  );

  // Initialize table (with empty data when loading)
  const table = useReactTable({
    data: data?.users ?? [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getRowId: (row) => String(row.id),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Show loading state
  if (loading) {
    return <LoadingState loadingText={t('EcgridPackageTest_Loading_Text')} />;
  }

  // Show error state
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refetch}
        errorTitle={t('EcgridPackageTest_Error_Title')}
        retryText={t('EcgridPackageTest_Error_Retry')}
      />
    );
  }

  // No data
  if (!data) {
    return null;
  }

  return (
    <PortalLayout ref={ref}>
      {/* Main Content Area */}
      <div className="p-6 space-y-4">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              {t('EcgridPackageTest_Content_Title')}
            </h2>
            <p className="text-muted-foreground">{t('EcgridPackageTest_Content_Description')}</p>
          </div>
        </div>

        {/* Filters */}
        <DataTableFilters table={table}>
          <InputFilter
            column={table.getColumn('name')}
            placeholder={t('EcgridPackageTest_Table_Filter_SearchName')}
          />
          <InputFilter
            column={table.getColumn('email')}
            placeholder={t('EcgridPackageTest_Table_Filter_SearchEmail')}
          />
          <SelectFilter
            column={table.getColumn('status')}
            title={t('EcgridPackageTest_Table_Column_Status')}
            options={data.statusOptions}
            width="w-[150px]"
          />
          <SelectFilter
            column={table.getColumn('role')}
            title={t('EcgridPackageTest_Table_Column_Role')}
            options={data.roleOptions}
            width="w-[150px]"
          />
        </DataTableFilters>

        {/* Data Table */}
        <DataTable
          table={table}
          showPagination={true}
          headerClassName="[&_th]:bg-primary [&_th]:text-white"
        />
      </div>
    </PortalLayout>
  );
});

EcgridPackageTestPage.displayName = 'EcgridPackageTestPage';
